import { useState, useEffect, useCallback } from "react";
import Button from "../../components/ui/Button";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import SplineEditor from "../../components/charts/SplineEditor";
import useProjectStore from "../../store/projectStore";
import { bladesApi } from "../../api/blades";
import useSaveShortcut from "../../hooks/useSaveShortcut";
import useBladeReadonly from "../../hooks/useBladeReadonly";

import { getChartColors } from "../../chartTheme";

function buildBetaLines(design) {
  const { LINE_COLORS } = getChartColors();
  const cls = design?.construction_lines;
  if (!cls?.length) return null;
  return cls.map((cl, i) => {
    const raw = cl.beta_spline?.control_points ?? [];
    return {
      name: cl.name,
      degree: cl.beta_spline?.degree ?? 4,
      control_points: raw.length ? enforceBetaConstraints(raw) : [[0, 0], [1, 0]],
      color: LINE_COLORS[i % LINE_COLORS.length],
      theta_start: cl.theta_start ?? 0,
      active: cl.active !== false,
    };
  });
}

// ── Constraint logic ──────────────────────────────────────────────────────────
// CP[0]   m = 0 locked
// CP[n-1] m = 1 locked

function enforceBetaConstraints(cps) {
  if (!cps.length) return cps;
  const r = cps.map(([m, b]) => [Math.min(1, Math.max(0, m)), b]);
  r[0] = [0, r[0][1]];
  r[r.length - 1] = [1, r[r.length - 1][1]];
  return r;
}

// ── (m, β) control point table ────────────────────────────────────────────────

function BetaTable({ controlPoints, onChange }) {
  const n = controlPoints.length;
  const emit = (rows) => onChange(enforceBetaConstraints(rows.map(r => [r.m, r.b])));
  const rows = controlPoints.map(([m, b]) => ({ m, b }));

  const isMlocked = (i) => i === 0 || i === n - 1;
  const isDelLocked = (i) => n <= 2 || i === 0 || i === n - 1;

  const updateCell = (i, col, val) => {
    if (isNaN(val)) return;
    const next = rows.map((r, j) => j === i ? { ...r, [col]: val } : r);
    emit(next);
  };

  const insertAfter = (i) => {
    const a = rows[i], b = rows[i + 1];
    const mid = {
      m: parseFloat(((parseFloat(a.m) + parseFloat(b.m)) / 2).toFixed(3)),
      b: parseFloat(((parseFloat(a.b) + parseFloat(b.b)) / 2).toFixed(2)),
    };
    emit([...rows.slice(0, i + 1), mid, ...rows.slice(i + 1)]);
  };

  const deleteRow = (i) => emit(rows.filter((_, j) => j !== i));

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();
    const parsed = text.trim().split(/\r?\n/).map(line => {
      const parts = line.split("\t");
      return {
        m: parseFloat(parts[0]?.replace(",", ".")) || 0,
        b: parseFloat(parts[1]?.replace(",", ".")) || 0,
      };
    }).filter(r => !isNaN(r.m) && !isNaN(r.b));
    if (parsed.length > 0) emit(parsed);
  }, [rows, emit]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="overflow-x-auto" onPaste={handlePaste}>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="w-6 border border-gray-200 px-1 py-1 text-gray-400 font-normal">#</th>
            <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">m [0–1]</th>
            <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">β [deg]</th>
            <th className="w-12 border border-gray-200"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const mLock = isMlocked(i);
            const delLock = isDelLocked(i);
            return (
              <tr key={i} className={mLock ? "bg-gray-50/60" : "hover:bg-bh-50/40"}>
                <td className="border border-gray-200 px-1 py-0.5 text-gray-300 text-center select-none">
                  {i + 1}
                </td>
                <td className="border border-gray-200 p-0">
                  {mLock
                    ? <span className="block px-2 py-1 text-gray-400">{row.m}</span>
                    : <input type="number" step="0.01" min="0" max="1" value={row.m}
                        onChange={e => updateCell(i, "m", parseFloat(e.target.value))}
                        className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50" />
                  }
                </td>
                <td className="border border-gray-200 p-0">
                  <input
                    type="number" step="0.1"
                    value={row.b}
                    onChange={e => updateCell(i, "b", parseFloat(e.target.value))}
                    className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50"
                  />
                </td>
                <td className="border border-gray-200 px-1 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {i < n - 1 && (
                      <button onClick={() => insertAfter(i)} title="Insert point after"
                        className="text-gray-300 hover:text-bh-400 transition-colors">
                        <PlusIcon className="h-3 w-3" />
                      </button>
                    )}
                    <button onClick={() => deleteRow(i)} disabled={delLock} title="Delete point"
                      className="text-gray-300 hover:text-red-400 transition-colors disabled:opacity-20">
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        onClick={() => insertAfter(n - 2)}
        className="mt-1 flex items-center gap-1 text-xs text-bh-500 hover:text-bh-700 px-1"
      >
        <PlusIcon className="h-3 w-3" /> Add row
      </button>
      <p className="text-gray-400 text-xs mt-1 px-1">Paste from Excel (m | β columns)</p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BetaPage() {
  const { activeDesign, saveConstructionLines, setActiveDesign } = useProjectStore();
  const { isReadOnly } = useBladeReadonly();
  const [betaLines, setBetaLines] = useState(() => buildBetaLines(activeDesign) ?? []);
  const [activeIdx, setActiveIdx] = useState(0);
  const [fixEnd, setFixEnd] = useState(activeDesign?.fix_end ?? true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loaded = buildBetaLines(activeDesign);
    if (loaded) setBetaLines(loaded);
    if (activeDesign?.fix_end != null) setFixEnd(activeDesign.fix_end);
  }, [activeDesign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCPChange = (newCPs) => {
    setBetaLines((ls) =>
      ls.map((l, i) => i === activeIdx ? { ...l, control_points: enforceBetaConstraints(newCPs) } : l)
    );
  };

  const onDegreeChange = (deg) => {
    setBetaLines((ls) =>
      ls.map((l, i) => i === activeIdx ? { ...l, degree: deg } : l)
    );
  };

  const onThetaChange = (val) => {
    setBetaLines((ls) =>
      ls.map((l, i) => i === activeIdx ? { ...l, theta_start: val } : l)
    );
  };

  const save = async () => {
    if (!activeDesign) return;
    setSaving(true);
    try {
      const [{ data: updatedDesign }] = await Promise.all([
        bladesApi.update(activeDesign.id, { fix_end: fixEnd }),
      ]);
      setActiveDesign(updatedDesign);
      const cls = activeDesign.construction_lines || [];
      const merged = cls.map((cl, i) => ({
        ...cl,
        theta_start: betaLines[i]?.theta_start ?? 0,
        beta_spline: {
          degree: betaLines[i]?.degree ?? 4,
          control_points: betaLines[i]?.control_points ?? [],
        },
      }));
      await saveConstructionLines(activeDesign.id, merged);
    } catch { /* 403 or network error — stay silent, UI already blocked */ }
    finally {
      setSaving(false);
    }
  };
  useSaveShortcut(save, { disabled: saving || isReadOnly });

  const active = betaLines[activeIdx];

  if (!betaLines.length) {
    return (
      <div className="p-6 text-sm text-gray-500">
        No construction lines found. Save the MECO contour first.
      </div>
    );
  }

  return (
    <div className="flex gap-0 h-full">
      {/* ── Left panel: stream surface selector ── */}
      <div className="w-48 shrink-0 border-r border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Stream surfaces
          </p>
        </div>

        {/* LE / TE toggle */}
        <div className="px-4 py-3 border-b border-gray-200 space-y-1.5">
          <p className="text-xs text-gray-500">β development starts at</p>
          <div className="flex gap-1.5">
            {[{ label: "Leading Edge", value: false }, { label: "Trailing Edge", value: true }].map(opt => (
              <button
                key={String(opt.value)}
                onClick={() => setFixEnd(opt.value)}
                className={`flex-1 text-xs rounded px-2 py-1 border transition-colors
                  ${fixEnd === opt.value
                    ? "bg-bh-600 text-white border-bh-600 font-medium"
                    : "bg-white text-gray-600 border-gray-300 hover:border-bh-400"}`}
              >
                {opt.value ? "TE" : "LE"}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 leading-tight">
            {fixEnd ? "Trailing Edge — integration TE → LE" : "Leading Edge — integration LE → TE"}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto py-2 space-y-0.5 px-2">
          {betaLines.map((l, i) => (
            <button
              key={l.name}
              onClick={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${i === activeIdx
                  ? "bg-bh-50 text-bh-700 border border-bh-200"
                  : l.active
                    ? "text-gray-600 hover:bg-gray-50"
                    : "text-gray-400 hover:bg-gray-50"}`}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                style={{ background: l.active ? l.color : "#9ca3af" }}
              />
              {l.name}
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-gray-200">
          <Button size="sm" className="w-full justify-center" onClick={save} loading={saving} disabled={isReadOnly} shortcut="Ctrl+S">
            Save
          </Button>
        </div>
      </div>

      {/* ── Centre panel: (m, β) table ── */}
      <div className="w-64 shrink-0 border-r border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-gray-800">{active?.name}</p>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-xs text-gray-400">degree</span>
            <input
              type="number"
              min="1"
              max="10"
              value={active?.degree ?? 4}
              onChange={e => onDegreeChange(Number(e.target.value))}
              className="w-12 text-xs text-right border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:border-bh-400 bg-white"
            />
          </div>
        </div>

        {/* θ_init row */}
        <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-600">θ<sub>init</sub></p>
            <p className="text-xs text-gray-400 leading-tight">
              angle at {fixEnd ? "TE" : "LE"}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <input
              type="number"
              step="0.1"
              value={active?.theta_start ?? 0}
              onChange={e => onThetaChange(parseFloat(e.target.value) || 0)}
              className="w-16 text-xs text-right border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:border-bh-400 bg-white"
            />
            <span className="text-xs text-gray-400">deg</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {active && (
            <BetaTable
              key={activeIdx}
              controlPoints={active.control_points}
              onChange={(newCPs) => onCPChange(newCPs)}
            />
          )}
        </div>
      </div>

      {/* ── Right panel: spline editor ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-800">Beta angle — {active?.name}</p>
          <p className="text-xs text-gray-400">
            Drag control points or edit table · m = meridional length [0–1] · β in degrees · starts at {fixEnd ? "TE" : "LE"} · locked boundary CPs snap back when dragged
          </p>
        </div>
        <div className="flex-1 p-3">
          {active && (
            <SplineEditor
              key={activeIdx}
              degree={active.degree}
              controlPoints={active.control_points}
              onChange={onCPChange}
              xLabel="Meridional length m [0–1]"
              yLabel="Beta [deg]"
              color={active.color}
              xRange={[0, 1]}
              yRange={[0, 45]}
              allLines={betaLines.map(l => l.active ? l : { ...l, color: "#d1d5db" })}
              activeIdx={activeIdx}
            />
          )}
        </div>
      </div>
    </div>
  );
}
